import { Star, Shield, Clock, CheckCircle, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const Headland = () => {
  return (
    <>
      <SEO
        title="Plumber & Electrician Headland AL | Ard's Plumbing & Electric"
        description="Trusted plumber and electrician in Headland, AL. Same-day service, licensed & insured. Serving Henry County. Call (334) 692-5537."
        keywords="plumber Headland AL, electrician Headland, plumbing repair Headland Alabama, electrical service Headland, Henry County plumber"
        canonical="/service-areas/headland-al"
        additionalSchema={{ '@context': 'https://schema.org', '@type': 'BreadcrumbList', 'itemListElement': [{ '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' }, { '@type': 'ListItem', 'position': 2, 'name': 'Service Areas', 'item': 'https://ardsdothan.com/service-areas' }, { '@type': 'ListItem', 'position': 3, 'name': 'Headland, AL', 'item': 'https://ardsdothan.com/service-areas/headland-al' }] }}
      />

      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full mb-6">
                <MapPin className="w-4 h-4" />
                Serving Headland, AL
              </div>
              <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
                HEADLAND<br />
                <span className="text-brand-yellow">PLUMBING & ELECTRIC</span>
              </h1>
              <p className="text-lg text-white/90 mb-8 max-w-lg">
                Reliable plumbing and electrical services in Headland, Alabama. 
                Professional service for homes and businesses throughout Henry County.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
                >
                  <Link to="/contact-us">Schedule Service</Link>
                </Button>
                <a
                  href="tel:3346925537"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-blue transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  (334) 692-5537
                </a>
              </div>
            </div>
            <div className="bg-white/10 rounded-3xl p-8">
              <h3 className="font-heading font-bold text-2xl text-white mb-6">Why Headland Chooses Ard's</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-white">
                  <div className="w-10 h-10 bg-brand-yellow/20 rounded-full flex items-center justify-center">
                    <Star className="w-5 h-5 text-brand-yellow" />
                  </div>
                  <span>4-Star Rated Service</span>
                </div>
                <div className="flex items-center gap-4 text-white">
                  <div className="w-10 h-10 bg-brand-yellow/20 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-brand-yellow" />
                  </div>
                  <span>Mon-Fri 8AM-5PM</span>
                </div>
                <div className="flex items-center gap-4 text-white">
                  <div className="w-10 h-10 bg-brand-yellow/20 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-brand-yellow" />
                  </div>
                  <span>Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-4 text-white">
                  <div className="w-10 h-10 bg-brand-yellow/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-brand-yellow" />
                  </div>
                  <span>65+ Years Experience</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading font-black text-3xl text-brand-black mb-6">
                SERVICES IN HEADLAND, AL
              </h2>
              <p className="text-gray-700 mb-6">
                Ard's Plumbing & Electric is proud to serve Headland and Henry County. 
                We provide fast, reliable plumbing and electrical services for the 
                Headland community and surrounding areas.
              </p>
              <ul className="space-y-3">
                {[
                  'Plumbing Repair & Installation',
                  'Electrical Services',
                  'Drain Cleaning',
                  'Water Heater Services',
                  'Panel Upgrades',
                  'Emergency Repairs',
                ].map((service, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-brand-blue flex-shrink-0" />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 rounded-3xl p-8">
              <h3 className="font-heading font-bold text-xl text-brand-black mb-4">
                HEADLAND SERVICE AREA
              </h3>
              <p className="text-gray-700 mb-4">
                We provide comprehensive service throughout Headland:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li>• Downtown Headland</li>
                <li>• All Headland neighborhoods</li>
                <li>• Henry County areas</li>
                <li>• Commercial districts</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-black text-2xl text-brand-black mb-2 text-center">
            OUR SERVICES IN HEADLAND
          </h2>
          <p className="text-gray-600 text-center mb-8">Explore what Ard's offers for Headland homes and businesses.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Plumbing Services', desc: 'Leak repair, drains & installations', path: '/services/plumbing' },
              { label: 'Electrical Services', desc: 'Panels, wiring & safety checks', path: '/services/electrical' },
              { label: 'Residential Services', desc: 'Home plumbing & electrical', path: '/services/residential' },
              { label: 'Commercial Services', desc: 'Business & property solutions', path: '/services/commercial' },
            ].map(({ label, desc, path }) => (
              <Link
                key={path}
                to={path}
                className="bg-white rounded-2xl p-6 text-center border border-gray-200 hover:border-brand-blue hover:shadow-md transition-all"
              >
                <h3 className="font-bold text-brand-black mb-1">{label}</h3>
                <p className="text-sm text-gray-500">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-brand-coral">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
            SERVICE IN HEADLAND
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Call now for fast, reliable plumbing and electrical service in Headland, AL.
          </p>
          <a
            href="tel:3346925537"
            className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full bg-white text-brand-coral font-bold text-xl hover:bg-gray-100 transition-colors"
          >
            <Phone className="w-6 h-6" />
            Call (334) 692-5537
          </a>
        </div>
      </section>
    </>
  );
};

export default Headland;
